import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ITenant, TENANT_KEY } from 'onego-header';
import { BehaviorSubject, Observable, firstValueFrom, of } from 'rxjs';
import { filter, map, shareReplay, skip, switchMap, tap } from 'rxjs/operators';
import {
    INamedSubjectType,
    ISubjectType,
    ITaskApiConfig,
    ITenantConfig,
    ITenantConfigPUT,
    IUser,
    IUserApiConfig,
    TaskApiService,
    TenantApiService,
    UserApiService,
} from 'shared';

import { LazyTranslateLoader } from '../../utils/lazy-translate-loader';
import { AppStateService } from './state';
import { UserScopedStorageService } from './user-scoped-storage.service';

const LAST_SELECTED_TENANTS_KEY = 'WORKSPACE_LAST_SELECTED_TENANTS';

export interface ITenantApiConfig {
    taskApi: ITaskApiConfig;
    userApi: IUserApiConfig;
}

@Injectable({ providedIn: 'root' })
export class TenantService {
    private readonly translate = inject(TranslateService);
    private readonly taskApi = inject(TaskApiService);
    private readonly userApi = inject(UserApiService);
    private readonly userScopedStorage = inject(UserScopedStorageService);
    private readonly appState = inject(AppStateService);
    private readonly tenantApi = inject(TenantApiService);

    private readonly _config: BehaviorSubject<ITenantApiConfig> = new BehaviorSubject<ITenantApiConfig>(null);

    private readonly _subjectTypes: BehaviorSubject<Array<INamedSubjectType>> = new BehaviorSubject<
        Array<INamedSubjectType>
    >([]);

    public readonly tenantConfig: Observable<ITenantConfig>;

    public readonly config = this._config.pipe(filter((config) => !!config));
    public readonly subjectTypes: Observable<Array<INamedSubjectType>> = this._subjectTypes.asObservable();

    public constructor() {
        this.appState.loggedInWithTenant.stream
            .pipe(
                tap((loggedIn) => {
                    if (loggedIn) this.refreshConfig();
                    else this._config.next(null);
                })
            )
            .subscribe();

        // Refresh everything on socket connect
        this.appState.socketStatus.distinctStream
            .pipe(filter((s) => s.status === 'CONNECTED'))
            .subscribe((_) => this.refreshSubjectTypes());

        // Save selected tenant on change
        this.appState.activeTenant.distinctStream
            .pipe(
                skip(1),
                switchMap((tenant) => this.userScopedStorage.set(TENANT_KEY, tenant?.id))
            )
            .subscribe();

        this.tenantConfig = this.appState.activeTenant.distinctStream.pipe(
            switchMap((tenant) => (tenant?.id ? this.tenantApi.getConfig(tenant.id) : of(null))),
            shareReplay()
        );
    }

    public async refreshConfig(): Promise<void> {
        const [taskApiConfig, userApiConfig] = await Promise.all([
            firstValueFrom(this.taskApi.getConfig()),
            firstValueFrom(this.userApi.getConfig()),
        ]);
        this._config.next({
            taskApi: taskApiConfig,
            userApi: userApiConfig,
        });
    }

    public async updateSubjectTypes(newTypes: Array<ISubjectType>): Promise<void> {
        const newNamedTypes = newTypes.map((type) => {
            this.applyTranslationsForSubjectType(type);
            return TenantService.mapSubjectTypeToNamedType(type);
        });
        const newIds = newTypes.map((type) => type.id);
        this._subjectTypes.next([
            ...this._subjectTypes.value.filter((type) => !newIds.includes(type.id)),
            ...newNamedTypes,
        ]);
    }

    public async refreshSubjectTypes(): Promise<Array<ISubjectType>> {
        // Get subject types
        const config: ITenantApiConfig = await firstValueFrom(this.config);
        const subjectTypes = config.taskApi.subjectTypes;
        // Wait for base translations to have loaded
        await firstValueFrom((this.translate.currentLoader as LazyTranslateLoader).translationsLoaded$);
        // Extract translations and append them
        subjectTypes.forEach((type) => this.applyTranslationsForSubjectType(type));
        // Map to named type
        const namedSubjectTypes = subjectTypes.map(TenantService.mapSubjectTypeToNamedType);
        // Publish subject types and return them
        this._subjectTypes.next(namedSubjectTypes);
        return subjectTypes;
    }

    public getSubjectTypeForId(id: string): Observable<INamedSubjectType> {
        return this.subjectTypes.pipe(map((its) => its.find((it) => it.id === id)));
    }

    public getSubjectTypeForIdSync(id: string): INamedSubjectType {
        return this._subjectTypes.value.find((it) => it.id === id);
    }

    public removeSubjectType(id: string): void {
        this._subjectTypes.next(this._subjectTypes.getValue().filter((type) => type.id !== id));
    }

    public selectTenant(user: IUser, tenant: ITenant): ITenant {
        if (
            // Allow setting null tenant for anyone
            (!tenant ||
                // Allow setting tenant for users that are part of that tenant
                (user && user.tenants.find((t) => t.id === tenant.id))) &&
            // Only update if different
            this.appState.activeTenantId.value !== tenant?.id
        ) {
            this.appState.activeTenantId.value = tenant?.id ?? null;
            if (tenant) this.storeLastSelectedTenant(tenant.id).subscribe();
        }

        return tenant;
    }

    public clearStoredTenant(): Observable<void> {
        return this.userScopedStorage.delete(TENANT_KEY);
    }

    public getStoredTenant(): Observable<string> {
        return this.userScopedStorage.get<string>(TENANT_KEY, { type: 'string' });
    }

    public switchTenant(tenantId: string): void {
        this.storeLastSelectedTenant(tenantId)
            .pipe(tap(() => (window.location.href = `/?tenant=${tenantId}`)))
            .subscribe();
    }

    public updateConfig(config: ITenantConfigPUT): Observable<ITenantConfig> {
        const tenantId = this.appState.activeTenant.value?.id;
        if (!tenantId) throw new Error('Attempted updating a tenant config without an active tenant.');
        return this.tenantApi.updateConfig(config, tenantId);
    }

    public getTenantsFromStorage(): Observable<Array<string>> {
        return this.userScopedStorage
            .get(LAST_SELECTED_TENANTS_KEY)
            .pipe(map((result: unknown) => (result ? (result as Array<string>) : [])));
    }

    private applyTranslationsForSubjectType(type: ISubjectType): void {
        if (type.name && typeof type.name !== 'object') return;
        Object.entries(type.name).forEach(([lang, name]) => {
            this.translate.setTranslation(lang, { subjectType: { [type.id]: name } }, true);
        });
    }

    private storeLastSelectedTenant(tenantId: string): Observable<void> {
        return this.getTenantsFromStorage().pipe(
            map((tenants) => [tenantId, ...tenants.filter((tid) => tid !== tenantId)]),
            switchMap((tenants) => this.userScopedStorage.set(LAST_SELECTED_TENANTS_KEY, tenants))
        );
    }

    private static mapSubjectTypeToNamedType(type: ISubjectType): INamedSubjectType {
        return {
            id: type.id,
            name: typeof type.name === 'object' ? `subjectType.${type.id}` : type.name,
            iconId: type.iconId,
            archived: type.archived,
        };
    }
}
